import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

// A native <dialog> wrapper, used instead of StyledModal/react-modal wherever a
// dialog's content must reach the SSR HTML.
//
// react-modal cannot server-render at all: Modal returns null when !canUseDOM,
// ModalPortal returns null while closed, and React's server renderer has no
// portals. A closed native <dialog>, by contrast, keeps its children in the DOM,
// so crawlers -- which never open modals -- can read them. No portal needed.
//
// showModal() also gives us strictly more than react-modal's ariaHideApp did:
// ariaHideApp only set aria-hidden on #root, leaving the background focusable,
// whereas the top layer makes everything outside the dialog fully inert (hidden
// from assistive tech AND unfocusable), traps Tab in both directions, and
// restores focus to the trigger on close. Do not add manual focus handling here.
//
// The top layer also means the dialog is positioned independently of its
// ancestors, so unlike a hand-rolled position:fixed overlay this cannot be
// broken by an ancestor transform, filter or overflow.

// Deliberately declares no `display`: the UA stylesheet's
// `dialog:not([open]) { display: none }` is what keeps the closed dialog
// invisible, and author styles beat UA styles in the cascade. Any display
// utility landing here -- Tailwind's `flex`, say -- would make the closed
// dialog VISIBLE. That is why `className` goes to Content below, not here,
// mirroring how StyledModal applies it to its own inner wrapper.
const StyledDialog = styled.dialog`
  /* Matches StyledModal's old modalStyles.content: transparent and borderless,
     free to size itself. Tailwind's preflight already zeroes dialog padding.
     Without the max-* overrides the UA's calc(100% - 6px - 2em) would clip the
     content's own 70vw/80vh sizing. */
  border: 0;
  background: transparent;
  /* The top layer detaches the dialog visually but NOT from CSS inheritance,
     which still follows the DOM tree -- so without this the dialog picks up
     text-align from whatever ancestor it happens to be rendered inside (the
     mortgage modal sits in a md:text-right price column, which pushed every
     unaligned label to the right). react-modal never hit this because it
     portaled to <body>; resetting here restores that same neutral context for
     any consumer. */
  text-align: left;
  max-width: 100vw;
  max-height: 100vh;
  overflow: visible;

  /* Replaces modalStyles.overlay. No z-index needed -- the top layer sits above
     every stacking context. */
  &::backdrop {
    background-color: rgba(0, 0, 0, 0.5);
  }
`;

// Same shape and rules as StyledModal's Content, so callers that pass a
// className keep rendering identically.
const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0;
  justify-content: ${(p) => (p.$centered ? 'center' : 'space-between')};
`;

const SsrDialog = ({
  isOpen,
  onClose,
  centered,
  className,
  children,
  ...rest
}) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // showModal()/close() are imperative and throw or reset focus if called
    // redundantly, so reconcile against the DOM's own open state.
    if (isOpen && !el.open) el.showModal();
    if (!isOpen && el.open) el.close();
  }, [isOpen]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Escape closes the dialog natively and fires `close`. Without mirroring
    // that back into React state, isOpen would stay true while the DOM is
    // closed -- and the dialog could never be reopened.
    //
    // This also fires for our own el.close() above; onClose is idempotent
    // (setting state to the value it already holds is a no-op), so that is
    // harmless.
    const syncClosed = () => {
      if (typeof onClose === 'function') onClose();
    };
    el.addEventListener('close', syncClosed);
    return () => el.removeEventListener('close', syncClosed);
  }, [onClose]);

  return (
    <StyledDialog ref={ref} {...rest}>
      <Content $centered={centered} className={className}>
        {children}
      </Content>
    </StyledDialog>
  );
};

export default SsrDialog;
